'use client';

import Link from 'next/link';
import { ShoppingCart, User, LogIn, UserPlus, Search, Menu, X, ChevronDown, ChevronLeft } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import useSepetStore from '@/store/sepet.store';
import useAuthStore from '@/store/auth.store';
import { useHydration } from '@/store/useHydration';
import { API_URL } from '@/lib/config';
import api from '@/lib/axios';
import Image from 'next/image';
import BButon from '@/components/ui/BButon';

interface Kategori {
  id: number;
  ad: string;
  slug: string;
  parent_id: number | null;
  sira: number;
  alt_kategoriler?: Kategori[];
}

interface UrunOneri {
  id: number;
  urun_ad: string;
  urun_slug?: string;
  kategori: { ad: string; slug: string };
  urun_resimleri: { dosya_yolu: string }[];
  urun_varyasyonlari: { satis_fiyati: number }[];
}

export default function Navbar() {
  const [menuAcik, setMenuAcik] = useState(false);
  const [aramaMetni, setAramaMetni] = useState('');
  const [anaKategoriler, setAnaKategoriler] = useState<Kategori[]>([]);
  const [aktifMega, setAktifMega] = useState<number | null>(null);
  const [kullaniciMenuAcik, setKullaniciMenuAcik] = useState(false);
  const [siteAdi, setSiteAdi] = useState('Karacaspor');
  const [siteLogo, setSiteLogo] = useState('');
  const [oneriAcik, setOneriAcik] = useState(false);
  const [oneriSonuclar, setOneriSonuclar] = useState<UrunOneri[]>([]);
  const [oneriYukleniyor, setOneriYukleniyor] = useState(false);
  const [mobilStack, setMobilStack] = useState<Kategori[]>([]);

  const kullaniciMenuRef = useRef<HTMLDivElement>(null);
  const megaZamanRef = useRef<NodeJS.Timeout | null>(null);
  const aramaRef = useRef<HTMLDivElement>(null);
  const hydrated = useHydration();

  const kalemler = useSepetStore((state) => state.kalemler);
  const toplamAdet = kalemler.reduce((t, k) => t + k.miktar, 0);
  const { girisYapildi, kullanici, cikisYap } = useAuthStore();

  useEffect(() => {
    api.get('/kategoriler').then((res) => {
      const ana = res.data
        .filter((k: Kategori) => k.parent_id === null)
        .sort((a: Kategori, b: Kategori) => a.sira - b.sira);
      setAnaKategoriler(ana);
    });
    api.get('/site-ayarlari').then((res) => {
      if (res.data.site_adi) setSiteAdi(res.data.site_adi);
      if (res.data.site_logo) setSiteLogo(res.data.site_logo);
    });
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (kullaniciMenuRef.current && !kullaniciMenuRef.current.contains(e.target as Node)) {
        setKullaniciMenuAcik(false);
      }
      if (aramaRef.current && !aramaRef.current.contains(e.target as Node)) {
        setOneriAcik(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!menuAcik) setMobilStack([]);
  }, [menuAcik]);

  useEffect(() => {
    if (!aramaMetni.trim() || aramaMetni.length < 2) {
      setOneriSonuclar([]);
      setOneriAcik(false);
      return;
    }
    const timer = setTimeout(async () => {
      setOneriYukleniyor(true);
      try {
        const res = await api.get(`/urunler?arama=${encodeURIComponent(aramaMetni)}&limit=6`);
        setOneriSonuclar(res.data.veri || []);
        setOneriAcik(true);
      } catch (err) { console.error(err); }
      setOneriYukleniyor(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [aramaMetni]);

  const aramaYap = (e: React.FormEvent) => {
    e.preventDefault();
    if (aramaMetni.trim()) {
      setOneriAcik(false);
      setMenuAcik(false);
      window.location.href = `/urunler?arama=${encodeURIComponent(aramaMetni)}`;
    }
  };

  const megaAc = (id: number) => {
    if (megaZamanRef.current) clearTimeout(megaZamanRef.current);
    setAktifMega(id);
  };

  const megaKapat = () => {
    megaZamanRef.current = setTimeout(() => setAktifMega(null), 300);
  };

  const megaIptal = () => {
    if (megaZamanRef.current) clearTimeout(megaZamanRef.current);
  };

  const mobilGir = (kategori: Kategori) => {
    if (kategori.alt_kategoriler && kategori.alt_kategoriler.length > 0) {
      setMobilStack((prev) => [...prev, kategori]);
    } else {
      window.location.href = `/${kategori.slug}`;
      setMenuAcik(false);
    }
  };

  const mobilGeriDon = () => setMobilStack((prev) => prev.slice(0, -1));

  const aktifMobilKategoriler = mobilStack.length === 0
    ? anaKategoriler
    : mobilStack[mobilStack.length - 1].alt_kategoriler || [];

  const adet = hydrated ? toplamAdet : 0;

  return (
    <header className="bg-white shadow-[var(--shadow-md)] sticky top-0 z-50">

      {/* === DESKTOP: Üst satır === */}
      <div className="hidden md:block px-6 py-3">
        <div className="flex items-center justify-between gap-6">
          {/* Logo */}
          <Link href="/" className="flex-shrink-0">
            {siteLogo ? (
              <Image
                src={`${API_URL}${siteLogo}`}
                alt={siteAdi}
                width={200}
                height={80}
                className="h-20 w-auto object-contain"
                style={{ width: '100%', height: 'auto' }}
              />
            ) : (
              <span className="text-2xl font-bold text-[var(--color-primary)]">{siteAdi}</span>
            )}
          </Link>

          {/* Arama */}
          <div ref={aramaRef} className="flex-1 max-w-2xl relative">
            <form onSubmit={aramaYap}>
              <div className="relative">
                <input
                  type="text"
                  value={aramaMetni}
                  onChange={(e) => setAramaMetni(e.target.value)}
                  onFocus={() => oneriSonuclar.length > 0 && setOneriAcik(true)}
                  placeholder="Ürün, kategori veya marka ara..."
                  className="w-full border-2 border-[var(--color-gray-200)] rounded-[var(--radius-full)] px-6 py-3 pr-14 text-base focus:outline-none focus:border-[var(--color-primary)] transition-colors"
                />
                {aramaMetni && (
                  <button type="button" onClick={() => { setAramaMetni(''); setOneriAcik(false); }}
                    className="absolute right-14 top-1/2 -translate-y-1/2 text-[var(--color-gray-400)] hover:text-[var(--color-gray-600)]">
                    <X className="w-4 h-4" />
                  </button>
                )}
                <button type="submit"
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-[var(--color-primary)] text-white p-2 rounded-full hover:bg-[var(--color-primary-dark)] transition">
                  <Search className="w-5 h-5" />
                </button>
              </div>
            </form>

            {/* Öneri Dropdown */}
            {oneriAcik && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-[var(--radius-xl)] shadow-[var(--shadow-xl)] border border-[var(--color-gray-200)] z-50 overflow-hidden">
                {oneriYukleniyor ? (
                  <div className="p-4 text-center text-sm text-[var(--color-gray-500)]">Aranıyor...</div>
                ) : oneriSonuclar.length === 0 ? (
                  <div className="p-4 text-center text-sm text-[var(--color-gray-500)]">Sonuç bulunamadı</div>
                ) : (
                  <>
                    {oneriSonuclar.map((urun) => (
                      <div key={urun.id}
                        onMouseDown={() => { setOneriAcik(false); setAramaMetni(''); window.location.href = urun.urun_slug ? `/${urun.urun_slug}` : `/urunler/${urun.id}`; }}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-[var(--color-gray-50)] transition border-b border-[var(--color-gray-100)] last:border-0 cursor-pointer">
                        <div className="relative w-12 h-12 bg-[var(--color-gray-100)] rounded-[var(--radius-lg)] overflow-hidden flex-shrink-0">
                          {urun.urun_resimleri?.[0] ? (
                            <Image
                              fill
                              src={`${API_URL.replace('/api', '')}${urun.urun_resimleri[0].dosya_yolu}`}
                              alt={urun.urun_ad}
                              className="object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Search className="w-4 h-4 text-[var(--color-gray-400)]" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[var(--color-gray-800)] truncate">{urun.urun_ad}</p>
                          <p className="text-xs text-[var(--color-gray-500)]">{urun.kategori?.ad}</p>
                        </div>
                        {urun.urun_varyasyonlari?.[0] && (
                          <p className="text-sm font-bold text-[var(--color-primary)] flex-shrink-0">
                            {urun.urun_varyasyonlari[0].satis_fiyati?.toLocaleString('tr-TR')} ₺
                          </p>
                        )}
                      </div>
                    ))}
                    <div
                      onMouseDown={() => { setOneriAcik(false); window.location.href = `/urunler?arama=${encodeURIComponent(aramaMetni)}`; }}
                      className="flex items-center justify-center gap-2 px-4 py-3 bg-[var(--color-gray-50)] hover:bg-[var(--color-gray-100)] transition text-sm font-medium text-[var(--color-primary)] cursor-pointer">
                      <Search className="w-4 h-4" /> Tüm sonuçları gör
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Sağ ikonlar */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <Link href="/sepet" className="flex items-center gap-2 p-2 hover:bg-[var(--color-gray-100)] rounded-[var(--radius-lg)] transition group">
              <div className="relative">
                <ShoppingCart className="w-8 h-8 text-[var(--color-gray-600)] group-hover:text-[var(--color-primary)] transition" />
                <span className="absolute -top-2 -right-2 bg-[var(--color-secondary)] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">{adet}</span>
              </div>
              <div>
                <p className="text-xs text-[var(--color-gray-400)]">Sepetim</p>
                <p className="text-base font-semibold text-[var(--color-gray-700)]">{adet} Ürün</p>
              </div>
            </Link>

            {hydrated && girisYapildi ? (
              <div className="relative" ref={kullaniciMenuRef}>
                <button onClick={() => setKullaniciMenuAcik(!kullaniciMenuAcik)}
                  className="flex items-center gap-2 p-2 hover:bg-[var(--color-gray-100)] rounded-[var(--radius-lg)] transition">
                  <div className="w-10 h-10 bg-[var(--color-primary)] rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-base">{kullanici?.ad?.[0]?.toUpperCase()}</span>
                  </div>
                  <div className="text-left">
                    <p className="text-xs text-[var(--color-gray-400)]">Merhaba</p>
                    <p className="text-base font-semibold text-[var(--color-gray-700)]">{kullanici?.ad}</p>
                  </div>
                  <ChevronDown className="w-5 h-5 text-[var(--color-gray-400)]" />
                </button>
                {kullaniciMenuAcik && (
                  <div className="absolute right-0 mt-2 w-52 bg-white rounded-[var(--radius-lg)] shadow-[var(--shadow-lg)] border border-[var(--color-gray-200)] py-1 z-50">
                    <Link href="/hesabim" className="flex items-center gap-2 px-4 py-3 text-sm text-[var(--color-gray-700)] hover:bg-[var(--color-gray-50)]">
                      <User className="w-4 h-4" /> Hesabım
                    </Link>
                    <Link href="/siparislerim" className="flex items-center gap-2 px-4 py-3 text-sm text-[var(--color-gray-700)] hover:bg-[var(--color-gray-50)]">
                      <ShoppingCart className="w-4 h-4" /> Siparişlerim
                    </Link>
                    <hr className="my-1 border-[var(--color-gray-200)]" />
                    <BButon variant="danger"className="w-full justify-start" onClick={cikisYap}>Çıkış Yap</BButon>
                  </div>
                )}
              </div>
            ) : hydrated ? (
              <div className="relative" ref={kullaniciMenuRef}>
                <button onClick={() => setKullaniciMenuAcik(!kullaniciMenuAcik)}
                  className="flex items-center gap-2 p-2 hover:bg-[var(--color-gray-100)] rounded-[var(--radius-lg)] transition">
                  <div className="w-10 h-10 bg-[var(--color-gray-100)] border-2 border-[var(--color-gray-300)] rounded-full flex items-center justify-center hover:border-[var(--color-primary)] transition">
                    <User className="w-5 h-5 text-[var(--color-gray-500)]" />
                  </div>
                  <div className="text-left">
                    <p className="text-xs text-[var(--color-gray-400)]">Hesap</p>
                    <p className="text-sm font-semibold text-[var(--color-gray-700)]">Giriş Yap</p>
                  </div>
                </button>
                {kullaniciMenuAcik && (
                  <div className="absolute right-0 mt-2 w-44 bg-white rounded-[var(--radius-lg)] shadow-[var(--shadow-lg)] border border-[var(--color-gray-200)] py-1 z-50">
                    <Link href="/giris" onClick={() => setKullaniciMenuAcik(false)}
                      className="flex items-center gap-2 px-4 py-3 text-sm text-[var(--color-gray-700)] hover:bg-[var(--color-gray-50)]">
                      <LogIn className="w-4 h-4" /> Giriş Yap
                    </Link>
                    <Link href="/kayit" onClick={() => setKullaniciMenuAcik(false)}
                      className="flex items-center gap-2 px-4 py-3 text-sm text-[var(--color-gray-700)] hover:bg-[var(--color-gray-50)]">
                      <UserPlus className="w-4 h-4" /> Kayıt Ol
                    </Link>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* === MOBİL === */}
      <div className="md:hidden px-4 py-3 flex items-center justify-between gap-3">
        <Link href="/" className="flex-shrink-0">
          {siteLogo ? (
            <Image
              src={`${API_URL}${siteLogo}`}
              alt={siteAdi}
              width={120}
              height={50}
              className="h-12 w-auto object-contain"
              style={{ width: '100%', height: 'auto' }}
            />
          ) : (
            <span className="text-xl font-bold text-[var(--color-primary)]">{siteAdi}</span>
          )}
        </Link>
        <div className="flex items-center gap-2">
          <Link href="/sepet" className="relative p-2">
            <ShoppingCart className="w-7 h-7 text-[var(--color-gray-600)]" />
            {adet > 0 && (
              <span className="absolute -top-1 -right-1 bg-[var(--color-secondary)] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">{adet}</span>
            )}
          </Link>
          {hydrated && girisYapildi ? (
            <Link href="/hesabim" className="p-2">
              <div className="w-8 h-8 bg-[var(--color-primary)] rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-sm">{kullanici?.ad?.[0]?.toUpperCase()}</span>
              </div>
            </Link>
          ) : hydrated ? (
            <Link href="/giris" className="p-2">
              <div className="w-8 h-8 bg-[var(--color-gray-100)] border-2 border-[var(--color-gray-300)] rounded-full flex items-center justify-center">
                <User className="w-4 h-4 text-[var(--color-gray-500)]" />
              </div>
            </Link>
          ) : null}
          <button onClick={() => setMenuAcik(!menuAcik)} className="p-2">
            {menuAcik ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
          </button>
        </div>
      </div>

      {/* Mobil arama */}
      <div className="md:hidden px-4 pb-3" ref={aramaRef}>
        <form onSubmit={aramaYap}>
          <div className="relative">
            <input type="text" value={aramaMetni} onChange={(e) => setAramaMetni(e.target.value)}
              onFocus={() => oneriSonuclar.length > 0 && setOneriAcik(true)}
              placeholder="Ürün, kategori veya marka ara..."
              className="w-full border-2 border-[var(--color-gray-200)] rounded-[var(--radius-full)] px-5 py-2.5 pr-12 text-sm focus:outline-none focus:border-[var(--color-primary)] transition-colors" />
            {aramaMetni && (
              <button type="button" onClick={() => { setAramaMetni(''); setOneriAcik(false); }}
                className="absolute right-12 top-1/2 -translate-y-1/2 text-[var(--color-gray-400)]">
                <X className="w-4 h-4" />
              </button>
            )}
            <button type="submit" className="absolute right-2 top-1/2 -translate-y-1/2 bg-[var(--color-primary)] text-white p-1.5 rounded-full">
              <Search className="w-4 h-4" />
            </button>
          </div>
        </form>
        {oneriAcik && oneriSonuclar.length > 0 && (
          <div className="absolute left-4 right-4 mt-1 bg-white rounded-[var(--radius-xl)] shadow-[var(--shadow-xl)] border border-[var(--color-gray-200)] z-50 overflow-hidden">
            {oneriSonuclar.map((urun) => (
              <Link key={urun.id} href={urun.urun_slug ? `/${urun.urun_slug}` : `/urunler/${urun.id}`}
                onClick={() => { setOneriAcik(false); setAramaMetni(''); setMenuAcik(false); }}
                className="flex items-center gap-3 px-4 py-3 hover:bg-[var(--color-gray-50)] border-b border-[var(--color-gray-100)] last:border-0">
                <div className="relative w-10 h-10 bg-[var(--color-gray-100)] rounded-lg overflow-hidden flex-shrink-0">
                  {urun.urun_resimleri?.[0] ? (
                    <Image
                      fill
                      src={`${API_URL.replace('/api', '')}${urun.urun_resimleri[0].dosya_yolu}`}
                      alt={urun.urun_ad}
                      className="object-cover"
                    />
                  ) : (
                    <Search className="w-4 h-4 m-auto mt-3 text-[var(--color-gray-400)]" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{urun.urun_ad}</p>
                  <p className="text-xs text-[var(--color-gray-500)]">{urun.kategori?.ad}</p>
                </div>
              </Link>
            ))}
            <Link href={`/urunler?arama=${encodeURIComponent(aramaMetni)}`} onClick={() => setOneriAcik(false)}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-[var(--color-gray-50)] text-sm font-medium text-[var(--color-primary)]">
              <Search className="w-4 h-4" /> Tüm sonuçları gör
            </Link>
          </div>
        )}
      </div>

      {/* === DESKTOP: Kategori Menüsü === */}
      <div className="border-t border-[var(--color-gray-200)] bg-white hidden md:block">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center">
            {anaKategoriler.map((kategori) => {
              const altKatSayisi = kategori.alt_kategoriler?.length ?? 0;

              return (
                <div key={kategori.id} className="relative"
                  onMouseEnter={() => megaAc(kategori.id)}
                  onMouseLeave={megaKapat}>
                  <Link href={`/${kategori.slug}`}
                    className={`flex items-center gap-1 px-5 py-4 text-base font-medium whitespace-nowrap transition border-b-2 ${
                      aktifMega === kategori.id
                        ? 'text-[var(--color-primary)] border-[var(--color-primary)]'
                        : 'text-[var(--color-gray-700)] border-transparent hover:text-[var(--color-primary)]'
                    }`}>
                    {kategori.ad}
                    {altKatSayisi > 0 && <ChevronDown className="w-4 h-4" />}
                  </Link>

                  {aktifMega === kategori.id && altKatSayisi > 0 && (
                    <div
                      className="absolute top-full left-0 bg-white z-50 rounded-b-[var(--radius-lg)] flex" style={{ width: Math.min(altKatSayisi * 240, 960), borderTop: "3px solid var(--color-primary)", boxShadow: "0 8px 32px rgba(0,0,0,0.18)", border: "1px solid #e2e8f0", borderTopColor: "var(--color-primary)" }}
                      onMouseEnter={megaIptal}
                      onMouseLeave={megaKapat}
                    >
                      {kategori.alt_kategoriler!.map((alt, idx) => {
                        const altItems = alt.alt_kategoriler ?? [];
                        const sonDegil = idx < altKatSayisi - 1;
                        return (
                          <div key={alt.id}
                            className={sonDegil ? 'border-r border-[var(--color-gray-200)]' : ''}
                            style={{ width: 240, flexShrink: 0, overflow: 'hidden' }}>
                            <Link href={`/${alt.slug}`}
                              className="flex items-center justify-between px-3 py-2 text-sm font-bold text-[var(--color-gray-800)] hover:text-[var(--color-primary)] bg-[var(--color-gray-50)] border-b border-[var(--color-gray-200)] transition">
                              {alt.ad}
                              {altItems.length > 0 && <span className="text-xs text-[var(--color-gray-400)] font-normal ml-1">{altItems.length}</span>}
                            </Link>
                            <div style={{ maxHeight: 380, overflowY: 'auto', scrollbarWidth: 'thin', scrollbarColor: '#e2e8f0 transparent' }}>
                              {altItems.map((alt3) => (
                                <Link key={alt3.id} href={`/${alt3.slug}`}
                                  className="flex items-center px-3 py-1 text-sm text-[var(--color-gray-600)] hover:text-[var(--color-primary)] hover:bg-[var(--color-gray-50)] border-b border-[var(--color-gray-50)] transition">
                                  <span className="text-[var(--color-gray-300)] mr-1.5 text-xs">›</span>
                                  {alt3.ad}
                                </Link>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* === MOBİL: Drawer === */}
      {menuAcik && (
        <div className="md:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMenuAcik(false)} />
          <div className="absolute top-0 right-0 bottom-0 w-80 max-w-[90vw] bg-white shadow-2xl flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-4 py-4 border-b border-[var(--color-gray-200)] bg-[var(--color-primary)]">
              {mobilStack.length > 0 ? (
                <button onClick={mobilGeriDon} className="flex items-center gap-2 text-white font-semibold">
                  <ChevronLeft className="w-5 h-5" />
                  <span>{mobilStack.length >= 2 ? mobilStack[mobilStack.length - 2].ad : 'Ana Menü'}</span>
                </button>
              ) : (
                <span className="text-white font-bold text-lg">Kategoriler</span>
              )}
              <button onClick={() => setMenuAcik(false)} className="text-white p-1">
                <X className="w-6 h-6" />
              </button>
            </div>
            {mobilStack.length > 0 && (
              <div className="px-4 py-3 bg-[var(--color-gray-50)] border-b border-[var(--color-gray-200)]">
                <Link href={`/${mobilStack[mobilStack.length - 1].slug}`}
                  onClick={() => setMenuAcik(false)}
                  className="flex items-center justify-between text-base font-bold text-[var(--color-primary)]">
                  Tüm {mobilStack[mobilStack.length - 1].ad}
                  <span className="text-sm font-normal">→</span>
                </Link>
              </div>
            )}
            <div className="flex-1 overflow-y-auto">
              {aktifMobilKategoriler.map((kategori) => (
                <button key={kategori.id} onClick={() => mobilGir(kategori)}
                  className="w-full flex items-center justify-between px-4 py-4 text-base font-medium text-[var(--color-gray-800)] border-b border-[var(--color-gray-100)] hover:bg-[var(--color-gray-50)] transition text-left">
                  <span>{kategori.ad}</span>
                  {kategori.alt_kategoriler && kategori.alt_kategoriler.length > 0 && (
                    <ChevronDown className="w-5 h-5 text-[var(--color-gray-400)] -rotate-90" />
                  )}
                </button>
              ))}
            </div>
            <div className="border-t border-[var(--color-gray-200)] p-4">
              {hydrated && girisYapildi ? (
                <div className="space-y-2">
                  <Link href="/hesabim" onClick={() => setMenuAcik(false)}
                    className="flex items-center gap-3 px-4 py-3 bg-[var(--color-gray-50)] rounded-[var(--radius-lg)] text-sm font-medium text-[var(--color-gray-700)]">
                    <User className="w-5 h-5" /> Hesabım
                  </Link>
                  <Link href="/siparislerim" onClick={() => setMenuAcik(false)}
                    className="flex items-center gap-3 px-4 py-3 bg-[var(--color-gray-50)] rounded-[var(--radius-lg)] text-sm font-medium text-[var(--color-gray-700)]">
                    <ShoppingCart className="w-5 h-5" /> Siparişlerim
                  </Link>
                  <BButon variant="danger" className="w-full justify-start" onClick={() => { cikisYap(); setMenuAcik(false); }}>
                    Çıkış Yap
                  </BButon>
                </div>
              ) : hydrated ? (
                <div className="flex gap-2">
                  <Link href="/giris" onClick={() => setMenuAcik(false)}
                    className="flex-1 flex items-center justify-center gap-2 py-3 border-2 border-[var(--color-primary)] text-[var(--color-primary)] rounded-[var(--radius-lg)] text-sm font-semibold">
                    <LogIn className="w-4 h-4" /> Giriş
                  </Link>
                  <Link href="/kayit" onClick={() => setMenuAcik(false)}
                    className="flex-1 flex items-center justify-center gap-2 py-3 bg-[var(--color-primary)] text-white rounded-[var(--radius-lg)] text-sm font-semibold">
                    <UserPlus className="w-4 h-4" /> Kayıt Ol
                  </Link>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
